import { h } from 'vue'

import { useRouter } from 'vue-router'

import ShorterUrlDialogContent from '@/components/frontend/shorter-list/dialog/ShorterUrlDialogContent.vue'

import { eventEmitter } from '@/utils/events/event-emitter'
import { notifySuccess } from '@/utils/ui/handleNotification'

import * as aggr from '@/api-generator/aggr'
import { feShortUrlsAPI, feUrlGroupingAPI } from '@/APIs'
import { ShorterDialogMode, SHORTER_DIALOG_CONFIG } from '@/constant/client/shorterDialog'
import { CommonEvent } from '@/constant/events'
import appStore from '@/stores'

import { triggerListRefresh } from './useListRefresh'

import type {
  ShorterUrlDialogOptions,
  SingleUrlSubmitData,
  GroupData,
  GroupUrlSubmitData,
  GroupUrlEditData,
  ShorterSingleFormModel
} from '@/types/shorterUrlForm'

export const useShorterUrlDialog = () => {
  const router = useRouter()
  const shortUrlStore = appStore.useShortUrlStore

  /**
   * 建立個別短網址
   */
  const handleCreateSingleUrl = async (submitData: SingleUrlSubmitData) => {
    const reqBody: aggr.ShortUrlCreateRq = {
      companyUuId: shortUrlStore.selectedCompanyUuId,
      companyOrganizationUuid: shortUrlStore.selectedOrganizationId,
      ...submitData
    }

    const { data } = await feShortUrlsAPI.createShortUrl(reqBody)

    if (data?.urlUid) {
      triggerListRefresh({ targetList: 'ShorterList' })
      notifySuccess('短網址已成功建立')
      router.push({
        name: 'shorter-list',
        query: {
          companyUuid: shortUrlStore.selectedCompanyUuId,
          orgId: shortUrlStore.selectedOrganizationId
        }
      })
    }
    return data
  }

  /**
   * 編輯個別短網址
   */
  const handleUpdateSingleUrl = async (submitData: SingleUrlSubmitData, editData: ShorterSingleFormModel) => {
    const reqBody: aggr.ShortUrlUpdateRq = {
      urlUid: editData.urlUid,
      mainCode: editData.mainCode,
      companyUuId: shortUrlStore.selectedCompanyUuId,
      companyOrganizationUuid: shortUrlStore.selectedOrganizationId,
      projectName: submitData.projectName,
      originalUrl: submitData.originalUrl,
      utm: submitData.utm,
      chineseTag: submitData.chineseTag,
      title: submitData.title,
      description: submitData.description,
      newImageUrl: submitData.newImageUrl || '',
      newUploadImageBase64: submitData.newUploadImageBase64 || '',
      hasUpdateImage: submitData.hasUpdateImage || false,
      directUrl: submitData.directUrl,
      enableTraceTapeId: submitData.enableTraceTapeId,
      orgTagId: submitData.orgTagId ?? null
    }

    await feShortUrlsAPI.updateShortUrl(reqBody)
    notifySuccess('短網址已成功更新')
  }

  /**
   * 建立個別短網址副本
   */
  const handleCopySingleUrl = async (data: SingleUrlSubmitData) => {
    return handleCreateSingleUrl(data)
  }

  /**
   * 群組內建立個別短網址
   */
  const handleCreateSingleUrlInGroup = async (submitData: SingleUrlSubmitData, groupData: GroupData) => {
    const reqBody: aggr.FEUrlGroupingSingleUrlCreateRq = {
      companyUuId: shortUrlStore.selectedCompanyUuId,
      companyOrganizationUuid: shortUrlStore.selectedOrganizationId,
      urlGroupId: groupData.urlGroupId,
      ...submitData
    }

    await feUrlGroupingAPI.createUrlGroupingSingleUrl(reqBody)
    notifySuccess('短網址已成功建立')
  }

  /**
   * 建立群組短網址
   */
  const handleCreateGroupUrls = async (submitData: GroupUrlSubmitData) => {
    const reqBody: aggr.FEUrlGroupingCreateRq = {
      companyUuId: shortUrlStore.selectedCompanyUuId,
      companyOrganizationUuid: shortUrlStore.selectedOrganizationId,
      ...submitData
    }

    await feUrlGroupingAPI.createUrlGrouping(reqBody)
    triggerListRefresh({ targetList: 'GroupList' })
    notifySuccess('群組短網址已成功建立')
    router.push({
      name: 'group-list',
      query: {
        companyUuid: shortUrlStore.selectedCompanyUuId,
        orgId: shortUrlStore.selectedOrganizationId
      }
    })
  }

  /**
   * 編輯群組短網址
   */
  const handleUpdateGroupUrls = async (submitData: GroupUrlSubmitData, editData: GroupUrlEditData) => {
    const reqBody: aggr.FEUrlGroupingUpdateRq = {
      companyUuId: shortUrlStore.selectedCompanyUuId,
      companyOrganizationUuid: shortUrlStore.selectedOrganizationId,
      urlGroupId: editData.urlGroupId,
      ...submitData
    }

    await feUrlGroupingAPI.updateUrlGrouping(reqBody)
    notifySuccess('群組短網址已成功更新')
  }

  /**
   * 根據 mode 決定要執行的 action
   */
  const getSubmitHandler = (options: ShorterUrlDialogOptions) => {
    const { mode, editData, groupData, groupEditData } = options

    const handlerMap: Record<ShorterDialogMode, (data: SingleUrlSubmitData | GroupUrlSubmitData) => Promise<unknown>> =
      {
        [ShorterDialogMode.SINGLE_CREATE]: handleCreateSingleUrl as (
          data: SingleUrlSubmitData | GroupUrlSubmitData
        ) => Promise<unknown>,

        [ShorterDialogMode.SINGLE_EDIT]: (data) => {
          if (!editData) {
            throw new Error('Edit data is required for SINGLE_EDIT mode')
          }
          return handleUpdateSingleUrl(data as SingleUrlSubmitData, editData)
        },

        [ShorterDialogMode.SINGLE_COPY]: handleCopySingleUrl as (
          data: SingleUrlSubmitData | GroupUrlSubmitData
        ) => Promise<unknown>,

        [ShorterDialogMode.SINGLE_CREATE_IN_GROUP]: (data) => {
          if (!groupData) {
            throw new Error('Group data is required for SINGLE_CREATE_IN_GROUP mode')
          }
          return handleCreateSingleUrlInGroup(data as SingleUrlSubmitData, groupData)
        },

        [ShorterDialogMode.SINGLE_COPY_IN_GROUP]: (data) => {
          if (!groupData) {
            throw new Error('Group data is required for SINGLE_COPY_IN_GROUP mode')
          }
          return handleCreateSingleUrlInGroup(data as SingleUrlSubmitData, groupData)
        },

        [ShorterDialogMode.SINGLE_EDIT_IN_GROUP]: (data) => {
          if (!editData) {
            throw new Error('Edit data is required for SINGLE_EDIT_IN_GROUP mode')
          }
          return handleUpdateSingleUrl(data as SingleUrlSubmitData, editData)
        },

        [ShorterDialogMode.GROUP_CREATE]: handleCreateGroupUrls as (
          data: SingleUrlSubmitData | GroupUrlSubmitData
        ) => Promise<unknown>,

        [ShorterDialogMode.GROUP_EDIT]: (data) => {
          if (!groupEditData) {
            throw new Error('Group edit data is required for GROUP_EDIT mode')
          }
          return handleUpdateGroupUrls(data as GroupUrlSubmitData, groupEditData)
        }
      }

    return handlerMap[mode]
  }

  /**
   * 開啟短網址彈窗
   */
  const openShorterUrlDialog = (options: ShorterUrlDialogOptions) => {
    const { mode, editData, groupData, groupEditData, onSuccess, dialogHeight } = options

    // 獲取彈窗設定
    const config = SHORTER_DIALOG_CONFIG[mode]

    const handleSubmit = async (data: SingleUrlSubmitData | GroupUrlSubmitData, currentMode?: ShorterDialogMode) => {
      try {
        const actualMode = currentMode || options.mode
        const actualOptions = { ...options, mode: actualMode }
        const handler = getSubmitHandler(actualOptions)
        await handler(data)
        onSuccess?.()
        eventEmitter.emit(CommonEvent.CLOSE_DIALOG)
      } catch (error) {
        console.error('Submit failed:', error)
        throw error
      }
    }

    const handleCancel = () => {
      eventEmitter.emit(CommonEvent.CLOSE_DIALOG)
    }

    eventEmitter.emit(CommonEvent.OPEN_DIALOG, {
      title: config.title,
      children: () =>
        h(ShorterUrlDialogContent, {
          mode,
          editData: editData || null,
          groupData: groupData || null,
          groupEditData: groupEditData || null,
          onSubmit: handleSubmit,
          onCancel: handleCancel
        }),
      dialogProps: {
        width: '496px',
        destroyOnClose: true
      },
      height: dialogHeight || '550px',
      scrollable: true
    })
  }

  return {
    openShorterUrlDialog
  }
}
